const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');
const formatMessage = require('format-message');

const blockIconURI = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMzQiIGhlaWdodD0iMzQiIHJ4PSI4IiBmaWxsPSIjMjRBMTQ4Ii8+PHBhdGggZD0iTTExIDIxQzExIDE3LjEzNDIgMTQuMTM0IDI0IDE4IDI0VjE1QzE4IDEzLjg5NTQgMTguODk1NCAxMyAxOSAxM0MyMC4xMDQ2IDEzIDIxIDEzLjg5NTQgMjEgMTVWMjNDMjEgMjQuMTA0NiAyMS44OTU0IDI1IDIzIDI1QzI0LjEwNDYgMjUgMjUgMjQuMTA0NiAyNSAyM1YxNkMyNSAxNC44OTU0IDI1Ljg5NTQgMTQgMjcgMTRDMjguMTA0NiAxNCAyOSAxNC44OTU0IDI5IDE2VjI0QzI5IDI1LjEwNDYgMjkuODk1NCAyNiAzMSAyNkMzMi4xMDQ2IDI2IDMzIDI1LjEwNDYgMzMgMjRWMThDMzMgMTMuNTgxNyAyOS40MTgzIDEwIDI1IDEwQzIwLjU4MTcgMTAgMTcgMTMuNTgxNyAxNyAxOFYyMEMxNyAyMC41NTIyIDE2LjU1MjMgMjEgMTYgMjFDMTUuNDQ3NyAyMSAxNSAyMC41NTIyIDE1IDIwVjE2QzE1IDE0Ljg5NTQgMTQuMTA0NiAxNCAxMyAxNEMxMS44OTU0IDE0IDExIDE0Ljg5NTQgMTEgMTZWMjFaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==';
const menuIconURI = blockIconURI;

const VIDEO_STATE = {
    OFF: 'off',
    ON: 'on',
    ON_FLIPPED: 'on-flipped'
};

const HAND_PART_MENU = [
    ['tommelfinger', 'thumb'],
    ['pegefinger', 'indexFinger'],
    ['langefinger', 'middleFinger'],
    ['ringfinger', 'ringFinger'],
    ['lillefinger', 'pinky']
];

const HAND_SUB_PART_MENU = [
    ['base', 0],
    ['første kno', 1],
    ['anden kno', 2],
    ['spids', 3]
];

const loadScriptOnce = (url, globalName) => {
    const globalScope = typeof window !== 'undefined' ? window : global;
    if (globalScope[globalName]) {
        return Promise.resolve(globalScope[globalName]);
    }

    globalScope.__datakoderenScriptPromises = globalScope.__datakoderenScriptPromises || {};
    if (globalScope.__datakoderenScriptPromises[url]) {
        return globalScope.__datakoderenScriptPromises[url];
    }

    globalScope.__datakoderenScriptPromises[url] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(globalScope[globalName]);
        script.onerror = () => reject(new Error(`Kunne ikke hente scriptet ${url}`));
        document.head.appendChild(script);
    });

    return globalScope.__datakoderenScriptPromises[url];
};

class Scratch3HandSensingBlocks {
    constructor (runtime) {
        this.runtime = runtime;
        this._loopHandle = null;
        this._handposeModelPromise = null;
        this._latestPrediction = null;
        this._isRunningInference = false;
        this._dimensions = [480, 360];
        this._videoState = VIDEO_STATE.ON;
        this._videoTransparency = 50;

        this._startLoop();
    }

    getInfo () {
        return {
            id: 'handSensing',
            name: formatMessage({
                id: 'handSensing.categoryName',
                default: 'Hånd',
                description: 'Navn på håndsensing-udvidelsen'
            }),
            blockIconURI,
            menuIconURI,
            color1: '#24A148',
            color2: '#1C8A3B',
            color3: '#166C2E',
            blocks: [
                {
                    opcode: 'goToHandPart',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'handSensing.goToHandPart',
                        default: 'gå til [HAND_PART] [HAND_SUB_PART]',
                        description: 'Flyt sprite til en hånddel'
                    }),
                    arguments: {
                        HAND_PART: {
                            type: ArgumentType.STRING,
                            menu: 'HAND_PART',
                            defaultValue: 'thumb'
                        },
                        HAND_SUB_PART: {
                            type: ArgumentType.NUMBER,
                            menu: 'HAND_SUB_PART',
                            defaultValue: 3
                        }
                    }
                },
                {
                    opcode: 'handDetected',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'handSensing.handDetected',
                        default: 'kan se hånd?',
                        description: 'Om der er fundet en hånd'
                    })
                },
                {
                    opcode: 'setVideoState',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'handSensing.setVideoState',
                        default: 'sæt kamera [VIDEO_STATE]',
                        description: 'Tænd eller sluk kameraet'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: 'VIDEO_STATE',
                            defaultValue: VIDEO_STATE.ON
                        }
                    }
                },
                {
                    opcode: 'setVideoTransparency',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'handSensing.setVideoTransparency',
                        default: 'sæt kameragennemsigtighed til [TRANSPARENCY]',
                        description: 'Sæt kameraets gennemsigtighed'
                    }),
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 50
                        }
                    }
                }
            ],
            menus: {
                HAND_PART: HAND_PART_MENU.map(([text, value]) => ({text, value})),
                HAND_SUB_PART: HAND_SUB_PART_MENU.map(([text, value]) => ({text, value})),
                VIDEO_STATE: [
                    {text: 'tændt', value: VIDEO_STATE.ON},
                    {text: 'slukket', value: VIDEO_STATE.OFF},
                    {text: 'tændt spejlet', value: VIDEO_STATE.ON_FLIPPED}
                ]
            }
        };
    }

    _startLoop () {
        if (this._loopHandle) return;
        this._loopHandle = setInterval(() => {
            this._stepLoop().catch(() => {});
        }, 120);
    }

    async _ensureModel () {
        if (this._handposeModelPromise) return this._handposeModelPromise;

        this._handposeModelPromise = loadScriptOnce(
            'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@3.21.0/dist/tf.min.js',
            'tf'
        ).then(() => loadScriptOnce(
            'https://cdn.jsdelivr.net/npm/@tensorflow-models/handpose@0.0.7/dist/handpose.min.js',
            'handpose'
        )).then(handpose => handpose.load());

        return this._handposeModelPromise;
    }

    _getVideoDevice () {
        return this.runtime.ioDevices && this.runtime.ioDevices.video;
    }

    _applyVideoState () {
        const video = this._getVideoDevice();
        if (!video) return;

        if (this._videoState === VIDEO_STATE.OFF) {
            video.disableVideo();
            return;
        }

        video.enableVideo();
        video.mirror = this._videoState !== VIDEO_STATE.ON_FLIPPED;
        video.setPreviewGhost(this._videoTransparency);
    }

    async _stepLoop () {
        if (this._isRunningInference) return;
        const video = this._getVideoDevice();
        if (!video || this._videoState === VIDEO_STATE.OFF) return;

        const frame = video.getFrame({
            format: 'image-data',
            dimensions: this._dimensions
        });
        if (!frame) return;

        this._isRunningInference = true;
        try {
            const model = await this._ensureModel();
            const predictions = await model.estimateHands(frame, {flipHorizontal: false});
            this._latestPrediction = Array.isArray(predictions) && predictions.length ? predictions[0] : null;
        } finally {
            this._isRunningInference = false;
        }
    }

    _toScratchCoordinates (point) {
        return {
            x: point[0] - (this._dimensions[0] / 2),
            y: (this._dimensions[1] / 2) - point[1]
        };
    }

    goToHandPart (args, util) {
        if (!this._latestPrediction || !this._latestPrediction.annotations) return;
        const handPart = Cast.toString(args.HAND_PART);
        const partIndex = Math.max(0, Math.min(3, Cast.toNumber(args.HAND_SUB_PART)));
        const points = this._latestPrediction.annotations[handPart];
        if (!points || !points[partIndex] || !util.target || !util.target.setXY) return;

        const {x, y} = this._toScratchCoordinates(points[partIndex]);
        util.target.setXY(x, y, false);
    }

    handDetected () {
        return Boolean(this._latestPrediction);
    }

    setVideoState (args) {
        const state = Cast.toString(args.VIDEO_STATE);
        this._videoState = Object.values(VIDEO_STATE).includes(state) ? state : VIDEO_STATE.ON;
        this._applyVideoState();
    }

    setVideoTransparency (args) {
        this._videoTransparency = Math.max(0, Math.min(100, Cast.toNumber(args.TRANSPARENCY)));
        this._applyVideoState();
    }
}

module.exports = Scratch3HandSensingBlocks;
